import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import * as payloadsApi from '@/services/payloadsApi';
import type { ProfilePayloadSummary } from '@/types/api';

/**
 * Returns the raw payload catalog from the backend, including BOTH profile-
 * and command-kind entries. Most UI surfaces should prefer
 * {@link useProfilePayloadList} so that command-kind entries (e.g. DeviceLock,
 * EraseDevice) — which are not technically profile payloads — never leak into
 * configuration profile flows. Use this raw hook only when you intentionally
 * need access to commands.
 */
export function usePayloadList() {
  return useQuery({
    queryKey: ['payloads'],
    queryFn: async () => {
      const result = await payloadsApi.listPayloads();
      console.log('[usePayloadList] raw result:', result);
      // Handle case where result might be the envelope itself
      if (result && typeof result === 'object' && 'data' in result && Array.isArray((result as any).data)) {
        return (result as any).data;
      }
      return Array.isArray(result) ? result : [];
    },
  });
}

/**
 * Profile-only view of the payload catalog. Filters out any entry whose `kind`
 * is `"command"` (case-insensitive). This is the canonical hook for any
 * configuration profile UI (selectors, template matching, validators).
 */
export function useProfilePayloadList() {
  const query = usePayloadList();
  const data = useMemo<ProfilePayloadSummary[] | undefined>(
    () =>
      Array.isArray(query.data)
        ? (query.data.filter(
            (p: any) => ((p?.kind as string | undefined) || '').toLowerCase() !== 'command',
          ) as ProfilePayloadSummary[])
        : (query.data as ProfilePayloadSummary[] | undefined),
    [query.data],
  );
  return { ...query, data };
}

export function usePayloadSchema(kind: string, identifier: string, enabled = true) {
  return useQuery({
    queryKey: ['payloadSchema', kind, identifier],
    queryFn: () => payloadsApi.getPayloadSchema(kind, identifier),
    enabled: enabled && !!kind && !!identifier,
  });
}
